import * as React from 'react';

import classNames from 'classnames';

import type {BadgeColor} from '../badge/badge';
import {LoadingText} from '../loading-placeholder/loading-text';
import {useResizeObserver} from '../utils/hooks/use-resize-observer';

import {PageControlBarTabs} from './page-control-bar-tabs';
import {StatusBar} from './status-bar';
import type {StatusBarProps} from './status-bar';

import styles from './page-control-bar.module.css';

export type Tab = {
    id: string;
    label?: string;
    icon?: React.ComponentType<any> | string;
    // This allows us to use old and new icons and be able to size them correctly
    isLegacyIcon?: boolean;
    href?: string;
    // Occasionally the tab bar should override its default behavior
    // and link somewhere – usually for an upsell or in an embedded context
    externalLink?: string;
    component?: React.ReactNode;
    count?: string;
    onClick?: () => void;
    // This adds a checkbox to the right of the count. This is specific to the
    // activity modal which shows a checkbox to indicate that a summary is available.
    showCheckmark?: boolean;
    isDisabled?: boolean;
    isHidden?: boolean;
    hideTooltip?: boolean;
    // If defined, a badge with the specified color will be shown next to the tab
    badgeColor?: BadgeColor;
};

export type TabProps = {
    // using existing type since it must line up withTabbedContent
    // but this type could probably be improved in the future
    options: Tab[];
    selectedTab: string;
    onChangeTab?: (newTab: string) => void;
};

export type Variant = 'default' | 'ai';

type Props = {
    tabs: TabProps;
    getRef?: (ref?: HTMLDivElement | null | undefined) => void;
    statusInfo?: StatusBarProps;
    isStatusLoading?: boolean;
    // Reduces the y padding of each tab from 16px to 8px
    lessPadding?: boolean;
    noHorizontalPadding?: boolean;
    variant?: Variant;
    hasWhiteBackground?: boolean;
};

export function PageControlBar(props: Props) {
    const {hasWhiteBackground = true} = props;
    const [naturalBreakpoint, setNaturalBreakpoint] = React.useState<number | null | undefined>(
        null
    );

    const controlBarRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const [controlBarWidth] = useResizeObserver(controlBarRef.current);

    const tabsRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const [tabsWidth] = useResizeObserver(tabsRef.current);

    const statusBarRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const [statusBarWidth] = useResizeObserver(statusBarRef.current);

    React.useEffect(() => {
        if (tabsWidth && statusBarWidth && !naturalBreakpoint) {
            const totalWidth = tabsWidth + statusBarWidth;
            setNaturalBreakpoint(totalWidth);
        }
    }, [tabsWidth, statusBarWidth, naturalBreakpoint]);

    const shouldOnlyShowActiveTab = Boolean(
        controlBarWidth && naturalBreakpoint && controlBarWidth <= naturalBreakpoint
    );

    const controlBarClasses = classNames(
        'flex',
        'justify-sb',
        'border-bottom',
        'height-fit-content',
        {
            ['background-white']: hasWhiteBackground,
            ['px-16']: !props.noHorizontalPadding,
        }
    );

    return (
        <div
            ref={(ref) => {
                controlBarRef.current = ref;
                if (props.getRef) {
                    props.getRef(ref);
                }
            }}
            className={classNames(
                controlBarClasses,
                classNames(styles['control-bar-container'], {
                    [styles['control-bar-container--no-bg']]: !hasWhiteBackground,
                })
            )}
        >
            {props.tabs && (
                <PageControlBarTabs
                    tabs={props.tabs}
                    shouldOnlyShowActiveTab={shouldOnlyShowActiveTab}
                    lessPadding={props.lessPadding}
                    variant={props.variant}
                />
            )}
            {props.statusInfo && (
                <div className='py-8 flex flex-dir-col justify-center'>
                    {props.isStatusLoading ? (
                        <LoadingText height={35} width={250} />
                    ) : (
                        props.statusInfo && <StatusBar {...props.statusInfo} />
                    )}
                </div>
            )}
            {/*
                We need to know the natural breakpoint to determine if we should only show the active tab.
                This is the only way to find it (i.e., we need to have the browser render it but thankfully this should never be visible to the user).
            */}
            {!naturalBreakpoint && props.tabs && props.statusInfo && (
                <div className={styles['natural-breakpoint-finder-container']}>
                    {props.tabs && (
                        // @ts-expect-error
                        <div ref={tabsRef}>
                            <PageControlBarTabs tabs={props.tabs} variant={props.variant} />
                        </div>
                    )}
                    {props.statusInfo && (
                        // @ts-expect-error
                        <div className='py-8 flex flex-dir-col justify-center' ref={statusBarRef}>
                            {props.isStatusLoading ? (
                                <LoadingText height={35} width={250} />
                            ) : (
                                props.statusInfo && <StatusBar {...props.statusInfo} />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
